import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import FirebaseImage from "components/FirebaseImage";
import type { User, UserImage } from "components/userModel";
import { deleteUserImage } from "./deleteUserImage";

interface Props {
  user: User,
  refresh: () => Promise<void>,
}

export default function ImageEditComponent({ user, refresh }: Props) {
  const [images, setImages] = useState<UserImage[]>(user.images);
  const [deletingImages, setDeletingImages] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    filePath: string;
    fileName: string;
  } | null>(null);

  // Handle image click for zoom
  const handleImageClick = (filePath: string, fileName: string) => {
    setSelectedImage({ filePath, fileName });
    setOpenModal(true);
  };

  // Handle image delete
  const handleImageDelete = async (imageId: string, filePath: string) => {
    if (!window.confirm("画像を削除してもよろしいですか？")) {
      return;
    }

    setDeletingImages((prev) => [...prev, imageId]);

    await deleteUserImage(user.id, imageId, filePath);

    setImages((prev) => prev.filter((image) => image.id !== imageId));

    setDeletingImages((prev) => prev.filter((id) => id !== imageId));

    await refresh();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      {/* ID Images Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          身分証画像
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          画像をクリックすると拡大します
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
          {images.map((image) => (
            <Box
              key={image.id}
              sx={{
                position: "relative",
                width: 150,
                height: 150,
                border: "1px solid #ddd",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {deletingImages.includes(image.id) ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.1)"
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <IconButton
                  key={image.id}
                  onClick={() => handleImageClick(image.filePath, image.fileName)}
                  sx={{ p: 0 }}
                >
                  <FirebaseImage
                    storagePath={image.filePath}
                    alt={image.fileName}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              )}
              {!deletingImages.includes(image.id) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    display: "flex",
                    background: "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <Button
                    color="error"
                    variant="text"
                    onClick={() => handleImageDelete(image.id, image.filePath)}
                  >
                    削除
                  </Button>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Image Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="image-modal"
        aria-describedby="enlarged-id-image"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <div>
            {selectedImage && (
              <FirebaseImage
                storagePath={selectedImage.filePath}
                alt={selectedImage.fileName}
                style={{ maxWidth: "100%", maxHeight: "80vh" }}
              />
            )}
          </div>
          <Button sx={{ mt: 2 }} variant="contained" onClick={handleCloseModal}>
            閉じる
          </Button>
        </Box>
      </Modal>
    </>
  );
}
